import * as React from 'react';

import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';

import {
  DIMENSION_LABEL,
  inLevelOrder,
  levelLabel,
  weightLevels,
  type DemoDimension,
  type DimensionDraft,
} from './demographic';
import DistributionBar from './DistributionBar';

/**
 * One demographic dimension: which levels, and in what proportion.
 *
 * Picking levels and setting their shares are two different acts, so they get
 * two different controls. The set is chosen above; the distribution over it is
 * a bar below, where the shares are widths of one full bar rather than numbers
 * in a column that happen to add up.
 *
 * Changing which levels are selected re-weights all of them, because a
 * selection change is structural: age by population share, everything else
 * evenly. That is also where each dimension opens, so the bar is dragged away
 * from its weighting rather than reset back to it — the two preset buttons that
 * used to sit here were a second way to say what the form already said.
 *
 * Country gets a dropdown and everything else gets chips. Not a style choice:
 * country runs to dozens of levels and is the one open-ended dimension, while
 * age, gender and smoking are short closed sets where seeing every option at
 * once is the point.
 */

type Props = {
  dimension: DemoDimension;
  draft: DimensionDraft;
  /** levels this model offers; anything else renders as the reference */
  available: string[];
  /** optional context per level, e.g. how many panellists a country has */
  counts?: Record<string, number>;
  onChange: (next: DimensionDraft) => void;
};

/** More levels than chips can show without becoming a wall. */
const DROPDOWN_DIMENSIONS: DemoDimension[] = ['country'];

const DimensionEditor: React.FC<Props> = ({
  dimension,
  draft,
  available,
  counts,
  onChange,
}) => {
  const weights = draft ?? {};
  const label = DIMENSION_LABEL[dimension];
  // Display order is the vocabulary's, so the bar does not reshuffle itself
  // when a level is added.
  const selected = inLevelOrder(dimension, Object.keys(weights));

  const setSelection = (levels: string[]) =>
    onChange(weightLevels(dimension, inLevelOrder(dimension, levels)));

  const toggleLevel = (level: string) =>
    setSelection(
      level in weights
        ? selected.filter((l) => l !== level)
        : [...selected, level],
    );

  const optionLabel = (level: string) =>
    counts?.[level]
      ? `${levelLabel(level)} (${counts[level]})`
      : levelLabel(level);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label} distribution
      </Typography>

      {DROPDOWN_DIMENSIONS.includes(dimension) ? (
        <Autocomplete
          multiple
          size="small"
          options={available}
          value={selected}
          onChange={(_e, next) => setSelection(next as string[])}
          getOptionLabel={optionLabel}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={
                selected.length ? '' : `Add a ${label.toLowerCase()}`
              }
            />
          )}
          sx={{ mb: 1 }}
        />
      ) : (
        <Box sx={{ mb: 1 }}>
          {available.map((level) => (
            <Chip
              key={level}
              size="small"
              label={optionLabel(level)}
              color={level in weights ? 'primary' : 'default'}
              variant={level in weights ? 'filled' : 'outlined'}
              onClick={() => toggleLevel(level)}
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {selected.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'warning.main' }}>
          Choose at least one level.
        </Typography>
      ) : (
        <DistributionBar
          label={label}
          levels={selected}
          weights={weights}
          onChange={onChange}
        />
      )}
    </Box>
  );
};

export default DimensionEditor;
